import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Comprehensive ResizeObserver error suppression
// This is a known benign issue with React + Radix UI Select components
// It does not affect functionality and is safe to suppress
const resizeObserverErrRe = /ResizeObserver loop completed with undelivered notifications|ResizeObserver loop limit exceeded/i;

// Suppress console.error
const origError = window.console.error;
window.console.error = (...args) => {
  if (args && args.length > 0) {
    const errorMessage = typeof args[0] === 'string' ? args[0] : (args[0]?.message || String(args[0]));
    if (resizeObserverErrRe.test(errorMessage)) {
      return;
    }
  }
  origError.call(window.console, ...args);
};

// Suppress console.warn for ResizeObserver
const origWarn = window.console.warn;
window.console.warn = (...args) => {
  if (args && args.length > 0) {
    const warnMessage = typeof args[0] === 'string' ? args[0] : String(args[0]);
    if (resizeObserverErrRe.test(warnMessage)) {
      return;
    }
  }
  origWarn.call(window.console, ...args);
};

// Suppress global error event
window.addEventListener('error', (e) => {
  if (e.message && resizeObserverErrRe.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
}, true);

// Suppress unhandledrejection for ResizeObserver
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason && e.reason.message && resizeObserverErrRe.test(e.reason.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
}, true);

// Override ResizeObserver to catch and suppress errors at source
if (typeof ResizeObserver !== 'undefined') {
  const OriginalResizeObserver = ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        try {
          callback(entries, observer);
        } catch (e) {
          if (!resizeObserverErrRe.test(e.message)) {
            throw e;
          }
          // Suppress ResizeObserver loop errors
        }
      });
    }
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Service worker registration is intentionally DISABLED.
// A prior SW (with `sw.js` served as immutable/1yr) pinned some browsers to a stale bundle.
// `/sw.js` is now a kill-switch that unregisters any existing SW and clears caches; we do NOT
// register a new one here so nothing re-installs. (Re-introduce a properly cache-controlled SW later.)
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    // Proactively unregister any existing SW and clear caches so stale bundles can't be served.
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    }).catch(() => {});
    if (window.caches && caches.keys) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
    }
  });
}
